// Reuse the single Supabase client created in lib/supabase.js instead of
// creating a second one here. Two independent createClient() calls (this
// file previously had its own) both default to the same auth storage key,
// which Supabase's own GoTrueClient warns about — "Multiple GoTrueClient
// instances detected... may produce undefined behavior when used
// concurrently under the same storage key" — since two clients each
// independently manage (and can race on) the same persisted session.
import { supabase } from '../lib/supabase';

// Session management utilities
export const sessionUtils = {
  async refreshSession() {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      return { data, error };
    } catch (error) {
      console.error('❌ Exception during session refresh:', error);
      return { data: null, error };
    }
  },

  async validateAndRefreshSession() {
    
    const { data: { session }, error } = await supabase.auth.getSession();
    
    
    if (error) {
      console.error('❌ Error getting session:', error);
      return { isValid: false, session: null, error };
    }
    
    if (!session) {
      console.warn('⚠️ No session found');
      return { isValid: false, session: null, error: { message: 'No session found' } };
    }
    
    // Check if session is expired or about to expire (within 5 minutes)
    const expiryTime = new Date(session.expires_at * 1000);
    const now = new Date();
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
    
    
    if (expiryTime <= fiveMinutesFromNow) {
      const refreshResult = await this.refreshSession();
      
      if (refreshResult.error || !refreshResult.data?.session) {
        console.error('❌ Failed to refresh session:', refreshResult.error);
        return { isValid: false, session: null, error: refreshResult.error || { message: 'Session refresh failed' } };
      }
      
      return { isValid: true, session: refreshResult.data.session, error: null };
    }
    
    return { isValid: true, session, error: null };
  }
};

export { supabase };

/**
 * Build a display time string ("10:30 - 17:00") from the sessions table's
 * separate start_time/end_time columns. There is no single `session_time`
 * column in the schema — a previous version of this transform read
 * `session.session_time`, which never existed, so every session's time
 * silently fell back to "TBA" on Programme/SessionDetail/Admin, and
 * Timetable's time-string regex parse failed entirely, silently dropping
 * every session out of the day/time grid.
 */
const trimSeconds = (time) => (typeof time === 'string' ? time.slice(0, 5) : time);

const formatSessionTime = (startTime, endTime) => {
  const start = trimSeconds(startTime);
  const end = trimSeconds(endTime);
  if (start && end) return `${start} - ${end}`;
  if (start) return start;
  return 'TBA';
};

/**
 * Fetch all sessions from Supabase
 * @returns {Promise<Array>} Array of session objects
 */
export const fetchSessions = async (activeYear = undefined) => {
  try {
    let sessionsQuery = supabase
      .from('sessions')
      .select('*')
      .order('track', { ascending: true })
      .order('start_time', { ascending: true });

    if (activeYear) {
      sessionsQuery = sessionsQuery.eq('conference_year', activeYear);
    }

    const { data, error } = await sessionsQuery;

    if (error) {
      console.error('❌ [fetchSessions] Supabase error:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }

    
    // Transform Supabase data to match frontend expectations
    const transformedSessions = data?.map(session => ({
      id: session.id,
      title: session.title,
      speaker: session.speaker,
      presenters: session.speaker ? session.speaker.split(';').map(p => p.trim()) : [],
      description: session.description,
      abstract: session.abstract_content,
      start_time: session.start_time,
      end_time: session.end_time,
      location: session.location,
      room: session.room_details || session.location,
      building: session.building_name,
      track: session.track,
      day: session.day,
      date: session.session_date,
      sessionDate: session.session_date,
      sessionTime: session.session_time,
      format: session.session_format,
      topic: session.topic_category,
      category: session.topic_category || session.session_format || 'General',
      hasAbstract: session.has_abstract,
      viewDetails: session.has_abstract ? {
        abstractContent: session.abstract_content,
        references: session.abstract_references,
        level: session.session_level,
        ageGroup: session.age_group
      } : null,
      rawData: session.raw_data,
      status: session.status,
      cancellation_notes: session.cancellation_notes,
      submissionId: session.submission_id,
      sessionNumber: session.session_number,
      posterImageUrl: session.poster_image_url,
      // Add computed fields for compatibility
      formatted_time: formatSessionTime(session.start_time, session.end_time),
      time: formatSessionTime(session.start_time, session.end_time)
    })) || [];
    
    
    // Remove duplicates based on title + track combination (case-insensitive)
    const seenSessions = new Set();
    const uniqueSessions = [];

    transformedSessions.forEach(session => {
      const titleKey = session.title?.toLowerCase().trim();
      const trackKey = session.track?.toLowerCase().trim();
      const sessionKey = `${titleKey}|${trackKey}`;

      if (titleKey && !seenSessions.has(sessionKey)) {
        seenSessions.add(sessionKey);
        uniqueSessions.push(session);
      }
    });

    return uniqueSessions;
    
  } catch (error) {
    console.error('❌ Error in fetchSessions:', error);
    throw error;
  }
};

/**
 * Fetch a single session by ID
 * @param {number} sessionId - The session ID
 * @returns {Promise<Object|null>} Session object or null if not found
 */
export const fetchSessionById = async (sessionId) => {
  try {
    
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .eq('id', sessionId)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        console.warn('⚠️ Session not found:', sessionId);
        return null;
      }
      console.error('❌ Error fetching session:', error);
      throw error;
    }
    
    
    // Transform Supabase data to match frontend expectations (same as fetchSessions)
    const transformedSession = {
      id: data.id,
      title: data.title,
      speaker: data.speaker,
      presenters: data.speaker ? data.speaker.split(';').map(p => p.trim()) : [],
      description: data.description,
      abstract: data.abstract_content,
      start_time: data.start_time,
      end_time: data.end_time,
      location: data.location,
      room: data.room_details || data.location,
      building: data.building_name,
      track: data.track,
      day: data.day,
      date: data.session_date,
      sessionDate: data.session_date,
      sessionTime: data.session_time,
      format: data.session_format,
      topic: data.topic_category,
      category: data.topic_category || data.session_format || 'General',
      hasAbstract: data.has_abstract,
      viewDetails: data.has_abstract ? {
        abstractContent: data.abstract_content,
        references: data.abstract_references,
        level: data.session_level,
        ageGroup: data.age_group
      } : null,
      rawData: data.raw_data,
      status: data.status,
      cancellation_notes: data.cancellation_notes,
      submissionId: data.submission_id,
      sessionNumber: data.session_number,
      posterImageUrl: data.poster_image_url,
      // Add computed fields for compatibility
      formatted_time: formatSessionTime(data.start_time, data.end_time),
      time: formatSessionTime(data.start_time, data.end_time)
    };


    return transformedSession;

  } catch (error) {
    console.error('❌ Error in fetchSessionById:', error);
    throw error;
  }
};

/**
 * Fetch a session by session number
 * @param {string} sessionNumber - The session number (e.g., "1.1", "2.3")
 * @returns {Promise<Object|null>} Session object or null if not found
 */
export const fetchSessionByNumber = async (sessionNumber, activeYear = undefined) => {
  try {

    let sessionQuery = supabase
      .from('sessions')
      .select('*')
      .eq('session_number', sessionNumber);

    if (activeYear) {
      sessionQuery = sessionQuery.eq('conference_year', activeYear);
    }

    const { data, error } = await sessionQuery.single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        console.warn('⚠️ ROUTING FIX: Session not found by session_number:', sessionNumber);
        return null;
      }
      console.error('❌ ROUTING FIX: Error fetching session by session_number:', error);
      throw error;
    }
    
    
    // Transform Supabase data to match frontend expectations (same as fetchSessions)
    const transformedSession = {
      id: data.id,
      title: data.title,
      speaker: data.speaker,
      presenters: data.speaker ? data.speaker.split(';').map(p => p.trim()) : [],
      description: data.description,
      abstract: data.abstract_content,
      start_time: data.start_time,
      end_time: data.end_time,
      location: data.location,
      room: data.room_details || data.location,
      building: data.building_name,
      track: data.track,
      day: data.day,
      date: data.session_date,
      sessionDate: data.session_date,
      sessionTime: data.session_time,
      format: data.session_format,
      topic: data.topic_category,
      category: data.topic_category || data.session_format || 'General',
      hasAbstract: data.has_abstract,
      viewDetails: data.has_abstract ? {
        abstractContent: data.abstract_content,
        references: data.abstract_references,
        level: data.session_level,
        ageGroup: data.age_group
      } : null,
      rawData: data.raw_data,
      status: data.status,
      cancellation_notes: data.cancellation_notes,
      submissionId: data.submission_id,
      sessionNumber: data.session_number,
      posterImageUrl: data.poster_image_url,
      // Add computed fields for compatibility
      formatted_time: formatSessionTime(data.start_time, data.end_time),
      time: formatSessionTime(data.start_time, data.end_time)
    };


    return transformedSession;
    
  } catch (error) {
    console.error('❌ ROUTING FIX: Error in fetchSessionByNumber:', error);
    throw error;
  }
};

/**
 * Get unique values for filtering
 * @returns {Promise<Object>} Object containing unique days, tracks, etc.
 */
export const getFilterOptions = async (activeYear = undefined) => {
  try {

    let filterQuery = supabase
      .from('sessions')
      .select('day, track, topic_category, session_format, building_name');

    if (activeYear) {
      filterQuery = filterQuery.eq('conference_year', activeYear);
    }

    const { data, error } = await filterQuery;
    
    if (error) {
      console.error('❌ Error fetching filter options:', error);
      throw error;
    }
    
    // Extract unique values
    const days = [...new Set(data.map(s => s.day).filter(Boolean))];
    const tracks = [...new Set(data.map(s => s.track).filter(Boolean))];
    const topics = [...new Set(data.map(s => s.topic_category).filter(Boolean))];
    const formats = [...new Set(data.map(s => s.session_format).filter(Boolean))];
    const buildings = [...new Set(data.map(s => s.building_name).filter(Boolean))];
    
    
    return {
      days: days.sort(),
      tracks: tracks.sort(),
      topics: topics.sort(),
      formats: formats.sort(),
      buildings: buildings.sort()
    };
    
  } catch (error) {
    console.error('❌ Error in getFilterOptions:', error);
    throw error;
  }
};

/**
 * Search sessions by text
 * @param {string} searchTerm - The search term
 * @returns {Promise<Array>} Array of matching sessions
 */
export const searchSessions = async (searchTerm, activeYear = undefined) => {
  try {

    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }

    let searchQuery = supabase
      .from('sessions')
      .select('*')
      .or(`title.ilike.%${searchTerm}%,speaker.ilike.%${searchTerm}%,abstract_content.ilike.%${searchTerm}%,topic_category.ilike.%${searchTerm}%`)
      .order('start_time', { ascending: true });

    if (activeYear) {
      searchQuery = searchQuery.eq('conference_year', activeYear);
    }

    const { data, error } = await searchQuery;
    
    if (error) {
      console.error('❌ Error searching sessions:', error);
      throw error;
    }
    
    return data || [];
    
  } catch (error) {
    console.error('❌ Error in searchSessions:', error);
    throw error;
  }
};

/**
 * Update a session in Supabase
 * @param {number} sessionId - The session ID
 * @param {Object} updates - The fields to update
 * @returns {Promise<Object>} Updated session object
 */
export const updateSession = async (sessionId, updates) => {
  try {
    
    // Filter out any undefined or null values and invalid fields
    const cleanUpdates = {};
    const validFields = ['title', 'speaker', 'abstract_content', 'location', 'session_level', 'topic_category', 'status', 'cancellation_notes'];
    
    Object.keys(updates).forEach(key => {
      if (validFields.includes(key) && updates[key] !== undefined && updates[key] !== null) {
        cleanUpdates[key] = updates[key];
      } else if (!validFields.includes(key)) {
        console.warn('⚠️ Skipping invalid field:', key);
      }
    });
    
    
    if (Object.keys(cleanUpdates).length === 0) {
      console.warn('⚠️ No valid fields to update');
      return null;
    }
    
    const { data, error } = await supabase
      .from('sessions')
      .update(cleanUpdates)
      .eq('id', sessionId)
      .select()
      .single();
    
    if (error) {
      console.error('❌ Error updating session:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }
    
    return data;
    
  } catch (error) {
    console.error('❌ Error in updateSession:', error);
    throw error;
  }
};

/**
 * Delete a session from Supabase
 * @param {number} sessionId - The session ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteSession = async (sessionId) => {
  try {
    
    const { error } = await supabase
      .from('sessions')
      .delete()
      .eq('id', sessionId);
    
    if (error) {
      console.error('❌ Error deleting session:', error);
      throw error;
    }
    
    return true;
    
  } catch (error) {
    console.error('❌ Error in deleteSession:', error);
    throw error;
  }
};

/**
 * Delete multiple sessions from Supabase
 * @param {Array<number>} sessionIds - Array of session IDs
 * @returns {Promise<boolean>} Success status
 */
export const deleteSessions = async (sessionIds) => {
  try {
    
    const { error } = await supabase
      .from('sessions')
      .delete()
      .in('id', sessionIds);
    
    if (error) {
      console.error('❌ Error bulk deleting sessions:', error);
      throw error;
    }
    
    return true;
    
  } catch (error) {
    console.error('❌ Error in deleteSessions:', error);
    throw error;
  }
};

export default {
  fetchSessions,
  fetchSessionById,
  fetchSessionByNumber,
  getFilterOptions,
  searchSessions,
  updateSession,
  deleteSession,
  deleteSessions
};